import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { makeStyles, createStyles } from '@material-ui/core/styles';

import List from '@material-ui/core/List';

import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import Collapse from '@material-ui/core/Collapse';

import IconExpandLess from '@material-ui/icons/ExpandLess';
import IconExpandMore from '@material-ui/icons/ExpandMore';

import Badge from '@material-ui/core/Badge';
import AppMenuItemComponent from './AppMenuItemComponent';

const useStyles = makeStyles((theme) =>
    createStyles({
        menuItem: {
            padding: '0 5px 0 0',
            '&>a': {
                padding: '8px',
                color: '#fff',
                display: 'flex',
                width: '100%',
                textDecoration: 'none',
                '&.active': {
                    color: theme.palette.secondary.light,
                    pointerEvents: 'none',
                },
            },
        },
        menuItemIcon: {
            color: '#fff',
            minWidth: '36px',
        },
        badge: {
            marginRight: theme.spacing(1),
            background: theme.palette.secondary.light,
        },
    })
);

// React runtime PropTypes
export const AppMenuItemPropTypes = {
    name: PropTypes.string.isRequired,
    path: PropTypes.string,
    Icon: PropTypes.elementType,
    items: PropTypes.array,
    count: PropTypes.number,
};

// TypeScript compile-time props type, infered from propTypes
// https://dev.to/busypeoples/notes-on-typescript-inferring-react-proptypes-1g88
type AppMenuItemPropTypes = PropTypes.InferProps<typeof AppMenuItemPropTypes>;
type AppMenuItemPropsWithoutItems = Omit<AppMenuItemPropTypes, 'items'>;

// Improve child items declaration
export type AppMenuItemProps = AppMenuItemPropsWithoutItems & {
    items?: AppMenuItemProps[];
};

const AppMenuItem: React.FC<AppMenuItemProps> = (props) => {
    const { name, path, Icon, count, items = [] } = props;
    const classes = useStyles();
    const isExpandable = items && items.length > 0;
    const [open, setOpen] = React.useState(false);
    const { t } = useTranslation();

    const handleClick = () => {
        setOpen(!open);
    };

    const MenuItemRoot = (
        <AppMenuItemComponent customClassName={classes.menuItem} path={path} onClick={handleClick}>
            {!!Icon && (
                <ListItemIcon className={classes.menuItemIcon}>
                    <Icon />
                </ListItemIcon>
            )}
            <ListItemText primary={t(name)} inset={!Icon} />
            {!!count && (
                <div>
                    <Badge
                        badgeContent={count}
                        max={99}
                        color="secondary"
                        classes={{ colorSecondary: classes.badge }}
                    />
                </div>
            )}
            {isExpandable && !open && <IconExpandMore />}
            {isExpandable && open && <IconExpandLess />}
        </AppMenuItemComponent>
    );

    const MenuItemChildren = isExpandable ? (
        <Collapse in={open} timeout="auto" unmountOnExit>
            <Divider />
            <List component="div">
                {items.map((item, index) => (
                    <AppMenuItem {...item} key={index} />
                ))}
            </List>
        </Collapse>
    ) : null;

    return (
        <>
            {MenuItemRoot}
            {MenuItemChildren}
        </>
    );
};

export default AppMenuItem;
